<!-- 
    Copyright (C) 2025, Paul B. Lucas. ALL RIGHTS RESERVED.  Licensed to University of Illinois.

    The component implements the actual request form and is included in the requestLoan.vue 
    component in views/

    <Revisions>
        02/13/2025          pblucas         created
        04/03/3035          pblucas         changed asset list from all assets to issued assets on user as steward
                                            queries on 'steward-netid'

    </Revisions>

-->

<template>
   
    <div class="_fieldset" >
        
        <!-- employee personal identifying information (PII) and approver data -->
        <div class="_col_50" style="order: 1;">
            <p class="_p_section">Requestor Information</p>
            <p class="_p_diminished_grn">
                If any of the following personal information is incorrect, 
                please see your supervisor or correct the information on your 'Me' tab.
            </p>
            <div class="_gold_box">
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">Name</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value" >{{ agreement['employee-name'] }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">Job Title</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{ employee.title  }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">NetID</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{  employee.netid }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">Home Org</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{  employee['home-org'] }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">UIN</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{  employee.uin }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">Campus Mail Code</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{  employee.mailcode }}</p>
                    </div>
                </div>
                <br/>
                <p class="_p_interbox_title">Home Address</p>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">Street Address Line 1</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{ employee.address1  }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">Street Address Line 2</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{ employee.address2  }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">City</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{ employee.city  }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">State</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{ employee.state  }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">Postal Code</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{ employee.postcode }}</p>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <p class="_item_label">Country</p>
                    </div>
                    <div class="_scol_60">
                        <p class="_item_value">{{ employee.country  }}</p>
                    </div>
                </div>
                <br/>
            </div>
        </div> 

        <div class="_col_50" style="order: 2;">
            <p class="_p_section">Loaned Asset Location Information</p>
            <p class="_p_diminished_grn">
                Where the loaned assets will be located. <br/> This is required for State of Illinois auditing purposes.<br/>
            </p>
            <div class="_gold_box">
                <div class="_row">
                    <div class="_copy_address">
                        <button class="_copy_address_button"  tabindex="0"  area-label="Copy home address" 
                            aria-describedby="Allows user to specify home address on record as remote workplace" 
                            @click.prevent="$emit('homeAddressRequested', event)">Copy Home Address</button>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <label class="_input_label" for="set2_prop1">Address 1</label>
                    </div>
                    <div class="_scol_60">
                        <input id="set2_prop1" class="_input" type="text" area-label="Address 1 input"  
                            aria-describedby="Input for asset address, line 1" 
                            tabindex="0" v-model="agreement['asset-address1']" />
                    </div> 
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <label class="_input_label" for="set2_prop2">Address 2</label>
                    </div>
                    <div class="_scol_60">
                        <input id="set2_prop2" class="_input" type="text" area-label="Address 2 input"  
                            aria-describedby="Input for asset address, line 2" 
                            tabindex="0" v-model="agreement['asset-address2']" />
                    </div> 
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <label class="_input_label" for="set2_prop3">City</label>
                    </div>
                    <div class="_scol_60">
                        <input id="set2_prop3" class="_input" type="text" area-label="City input"  
                            aria-describedby="Input for remote workplace city" 
                            tabindex="0" v-model="agreement['asset-city']" />
                    </div> 
                </div>
                <div v-if="agreement['asset-country'] != 'United States' && agreement['asset-country'] != 'USA'">
                    <div class="_row">
                        <div class="_scol_40">
                            <label class="_input_label" for="set2_prop4">State</label>
                        </div>
                        <div class="_scol_60">
                            <input id="set2_prop4" class="_input" v-model="agreement['asset-state']"  
                                aria-label="State/Territory input for non-USA"   
                                aria-description="Dropdown list to select state or territory if not in  USA"   
                            />
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="_row">
                        <div class="_scol_40">
                            <label class="_input_label" for="set2_prop4">State</label>
                        </div>
                        <div class="_scol_60">
                            <select id="set2_prop4" class="_select" v-model="agreement['asset-state']">
                                <option id="set2_prop4" class="_type_list" v-for="(item, idx) in aStates" 
                                    aria-label="State/Territory selection options" 
                                    aria-description="Dropdown list to select state or territory if in USA"
                                    :value="item"> {{item}} 
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <label class="_input_label" for="set2_prop5">Postcode (Zipcode)</label>
                    </div>
                    <div class="_scol_60">
                        <input id="set2_prop5" class="_input" type="text" area-label="Postal code input"  
                            aria-describedby="Input for asset postcode" 
                            tabindex="0" v-model="agreement['asset-postcode']" />
                    </div> 
                </div>
                <div class="_row">
                    <div class="_scol_40">
                        <label class="_input_label" for="set2_prop6">Country</label>
                    </div>
                    <div class="_scol_60">
                        <input id="set2_prop6" class="_input" type="text" area-label="Country input"  
                            aria-describedby="Input for asset country" 
                            tabindex="0" v-model="agreement['asset-country']" />
                    </div> 
                </div>
            </div>
        </div>
    </div>
    <div class="_fieldset" >   
        <!--  contacts  -->
        <div class="_col_50" style="order: 1;">
            <p class="_p_section">Requestor Contact Information</p>
            <p class="_p_diminished_grn">
                Contact information may be edited on your 'Me' tab.
            </p>
            <!--  display any contacts already loaded -->          
            <div class="_gold_box"  v-if="employee.contacts.length > 0">
                <div v-for="(_,idx) in employee.contacts">
                    <div class="_row">
                        <div class="_scol_39">
                            <p class="_item_label">{{ capitalizeType(employee.contacts[idx].type) }}</p>
                        </div>
                        <div class="_scol_39">
                            <p class="_item_value">{{ employee.contacts[idx].value }}</p>
                        </div>    
                    </div>
                </div>
            </div>
        </div>
        <div class="_col_50" style="order: 2;">
            <p class="_p_section">Term of Agreement</p>
            <p class="_p_diminished_grn">
                Loan agreements may be up to 2 years per policy.
            </p>
            <!--  display any contacts already loaded -->          
            <div class="_gold_box">
                <div class="_row">
                    <div class="_scol_40">
                        <label class="_input_label" for="set3_prop1">Term (months)</label>
                    </div>
                    <div class="_scol_60">
                        <input id="set3_prop1" class="_input" type="text" 
                            area-label="Agreement term in months"  
                            aria-describedby="Input for agreement term in months" 
                            tabindex="0" v-model="agreement['validity-period']" 
                            @click="validateTerm"   
                            @keydown.enter="validateTerm"/>
                    </div> 
                    </div>
            </div>
        </div>
    </div>
    <br/>
    <p class="_p_section">Employee Provided Commentary</p>
    <div class="_fieldset_2">
        <div class="_col_50" style="order: 1;">              
            <div class="_gold_box" >        
                <div class="_row">
                    <!-- area for the loan reason -->
                    <label class="_item_label" for="set4_prop1">Loan Reason:</label>
                    <textarea id="set4_prop1" class="_textarea" required 
                        v-model="agreement['loan-reason']">
                    </textarea>  
                </div>
            </div>
        </div>
        <div class="_col_50" style="order: 2;"> 
            <div class="_gold_box" >
                <div class="_row">
                    <!-- cagreement notes -->
                    <label class="_item_label" for="set4_prop2">Relevant Notes:</label>
                    <textarea id="set4_prop2" class="_textarea" required 
                        v-model="agreement['agreement-notes']">
                    </textarea>  
                </div>
            </div>
        </div>
    </div>
    <!--
    / >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    / LIST ISSUED ASSETS AND SELECT ASSETS TO BORROW (LOANED ASSETS)
    / >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    -->   
    <div class="_fieldset">
        <!-- issued university assets (array) -->
        <div class="_scol_99" style="order: 1;">
            <p class="_p_section">Issued University Assets</p>
            <p class="_p_diminished_grn">
                Please check any asset issued to you that you wish to place on loan,
                complete the balance of the form, then press 'Submit'.
            </p>
            <div class="_row">
                <div class="_blue_box" v-if="issuedAssets.length > 0">
                    <!-- container for existing listed assets -->
                    <div class="_assets">
                        <!-- row for table headings [type, tag, s/n, ,description, delete]-->
                        <div class="_row">
                            <div class="_scol_20">
                                <p class="_item_label">Request Loan</p>
                            </div>
                            <div class="_scol_20">
                                <p class="_item_label">Type</p>
                            </div>
                            <div class="_scol_12">
                                <p class="_item_label">Tag/ID</p>
                            </div>
                            <div class="_scol_12">
                                <p class="_item_label">Serial No.</p>
                            </div>
                            <div class="_scol_35">
                                <p class="_item_label">Description</p>
                            </div>
                            <div class="_scol_15">
                                <p class="_item_label">&nbsp;</p>
                            </div>
                        </div>
                        <div class="_row" v-for="(asset, idx) in issuedAssets">
                            <div class="_scol_12" style="text-align: center;">
                                <input class="_check" type="checkbox" v-model="issuedAssets[idx].selected" 
                                    tabindex="0"
                                    aria-label="Check to include in deployed assets" 
                                    aria-describedby="Allows user to select assets for deployment"  />
                            </div>
                            <div class="_scol_20">
                                <p class="_item_value">{{ issuedAssets[idx]['asset-type'] }}</p>
                            </div>
                            <div class="_scol_12">
                                <p class="_item_value">{{ issuedAssets[idx]['tag'] }}</p>
                            </div>
                            <div class="_scol_12">
                                <p class="_item_value">{{ issuedAssets[idx]['serial-no'] }}</p>
                            </div>
                            <div class="_scol_35">
                                <p class="_item_value_ellipsis">{{ issuedAssets[idx]['description'] }}</p>
                            </div>
                            
                        </div>
                    </div>    
                </div >
                <div v-else>
                    <p class="_assets_warning">
                        You have no assets issued to you and listed, therefor cannot requests loans on them.
                    </p>
                </div>
            </div>
        </div>
    </div>  
            
    <div class="_fieldset">
        <!-- legal: compliance and consent -->
        <div class="_scol_99" style="order: 1;"> 
        <p class="_p_section">Acceptance and Compliance</p>
            <div class="_gold_box">
                <div class="_row">
                    <div class="_scol_4">
                        <input id="agree1" type="checkbox" class="_checkbox" tabindex="0" 
                            aria-label="User consents to loan agreement" 
                            aria-describedby="Checking this box indicates agreement with this loan agreement and is required to submit."
                            v-model="agreement['policy-consent']" value=true>
                    </div>
                    <div class="_scol_90">
                        <label class="_item_value" for="agree1">By checking this box, I agree to adhere to the policies for loaned equipment and consent to the terms and conditions.</label>
                    </div>
                </div>
                    <div class="_row">
                    <div class="_scol_4">
                        <input id="agree2" type="checkbox" class="_checkbox" tabindex="0" 
                            aria-label="User checkbox for acceptable use policy agreement" 
                            aria-describedby="User checking this box indicates reading and agreement with acceptable use policy in CAM."
                            v-model="agreement['security-consent']" value=true>
                    </div>
                    <div class="_scol_90">
                        <label class="_item_value" for="agree2" >By checking this box, I certify that I have read the computer acceptable use policy and agree to the department's security policy.</label>
                    </div>
                </div>
   
            </div>
        </div>
    </div>
    <br />
      
</template>

<script>
    import {ref}                        from  'vue'
    import useNextAvailableNumberAPI    from  '@/composables/useNextAvailableNumberAPI'
    import useArchiBusAPI               from  '@/composables/useArchiBusAPI'
    import useDateHelpers               from  '@/composables/useDateHelpers'

    // import enumerations
    import SysConfigs                   from '@/configuration/form-options.json'

    export default {
        //  data to be loaded from authentication user where possible
        components: {
        },
        props:  [
            // note that this form handles parts of both agreement and employee
           'agreement',
           'employee',
           'issuedAssets',
           'claims',
           'gat'
        ],
        emits: [
            // used to update employee props since they are part of store and can only
            // be mutated via handlers
            'homeAddressRequested'
        ],
        setup(props) {
            const sysConfigs    =   ref([])
            sysConfigs.value    =   SysConfigs 

              return {sysConfigs}  
        },
        data() {
            return {

                // temporary working variables for forms
                aStates:        [],             // see beforeMount        
                
                // form state control
                bViewAgreementsForm:  false
                 
            }
        },

        
        beforeMount() {

            // need to pull the configuration arrays used in the options from json file on the local src
            //
            // Config arrays:
            this.aStates            =   this.sysConfigs['states-territories']
          
        },
        mounted() {
            //console.log("RWAForm:: mounted: is-hybrid: "+this.agreement['is-hybrid'])
        },
        updated()   {
            //console.log("RWAForm:: updated: is-hybrid: "+this.agreement['is-hybrid'])
            //console.log("RWAForm:: updated: bShowOfficeForm: "+this.bShowOfficeForm)
        },
        
        methods: {
            prettyDate(date)     {
                const { makePrettyDate }      =   useDateHelpers()
                return makePrettyDate(date)
            },
            // validate agreement validity term
            validateTerm() {
                if(this.agreement['validity-period'] > 24)
                this.agreement['validity-period'] = 24
            },

            // FUNCTION: to fixup class typography to human rfeadable
            // class types are XXX_YYY
            //
            capitalizeType(item) {
                item = item.replaceAll('_',' ')
                return item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
            }

        },
    }

</script>

<style scoped>
   
    /* text styling for responsive form */
    ._fieldset {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._fieldset_2 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 1px 0 0 0;
        padding: 0;
    }
    ._gold_box {
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #f9da98;
        margin: 0 10px 0 0;
        padding: 5px;
    }
    ._blue_box {
        width: 100%;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;  
        background-color: #BFC9D9;
        margin: 0 10px 0 0;
        padding: 5px;
    }
    ._assets_warning {
        text-align: center;
        color: #FF5F05;
        margin: 25px auto;
        padding: 5px;
    }
    ._hours_form    {
        background-color:#eee;
        padding: 2px;
    }
    ._assets {
        margin: 0;
        padding: 0;
    }
    /* inline controls with icons   */
    ._inline_icon {
        display: inline-block;
        padding: 0 5px 0 0;
        height: 16px;  
    }
    ._inline_icon_right {
        height: 10px;
        margin: 0 10px 0 0;
    }
    ._p_section {
        width: 100%;
        text-align: left;
        font-size:  0.9em;
        font-weight: bold;
        color: #13294B;
        margin: 5px 0 0 0;
        padding: 2px; 
    }
    .fieldset_section {
        width: 100%;
        margin: 0;
        padding: 2px;
    }
    ._p_interbox_title {
        text-align: left;
        font-size:  0.8em;
        font-weight: bold;
        color: #006230;
        margin: 5px 0 0 0;
        padding: 2px; 
    }
    ._p_diminished {
        text-align: left;
        font-size:  0.8em;
        color: #13294B;
        margin: 0;
        padding: 2px;
    }
    ._p_diminished_grn {
        text-align: left;
        font-size:  0.8em;
        color: #006230;
        margin: 0;
        padding: 2px;
    }
    ._red_border {
        border-style: solid;
        border-width: 5px;
        border-color: red;
        margin: 5px 0 0 0;
        padding: 0 0 8px 0;
    }
    /* special action buttons on forms */
    ._copy_address {
        margin: 0;
        padding: 2px;
        float: right;
    }
    ._generate_id {
        margin: 0;
        padding: 2px;
        float: right;
    }
    ._copy_address_button    {
        margin: 0;
        padding: 0 2px;
        font-size: 0.7em;
        background-color:#13294B;
        color: #E8E9EB;
        border-style: solid;
        border-width: 1px;
        border-color: #13294B;
        border-radius: 4px;
    }
    ._copy_address_button:hover    {
        background-color:#E8E9EB;
        color: black;
    }
    ._get_tag_button {
        margin: 0;
        padding: 0 2px;
        font-size: 0.7em;
        background-color: #13294B;
        color: #E8E9EB;
        border-style: solid;
        border-width: 2px;
        border-color: black;
        border-radius: 4px;
    }
    ._get_tag_button:hover {
        background-color:#E8E9EB;
        color: black;  
    }
    ._checks {
        width: 5%;
        text-align: center;
        margin: 0 auto;
        padding: 0;
    }
    ._pid_label {
        font-size: 0.9em;
        font-weight: normal;
        text-align: left;
        color: #13294B;
        margin: 0;
        padding: 2px 5px;
    }
    ._check {  
        width: 25px;  
        margin: 0 auto;
        padding: 0 6px;

    }

    /* responsive column specification */
    ._col_50 {
        float: left;
        text-align: left;
        width: 50%;
        margin: 5px 10px 0 0;
    }
    ._scol_4 {
        float: left;
        width: 4%;
        margin: 5px 0 0 0;
    }
    ._scol_8 {
        float: left;
        width: 8%;
        margin: 5px 0 0 0;
    }
    ._scol_12 {
        float: left;
        width: 12%;
        margin-top: 5px;
    }
    ._scol_15 {
        float: left;
        width: 15%;
        margin-top: 5px;
    }
    ._scol_20 {
        float: left;
        width: 20%;
        margin-top: 5px;
    }
    ._scol_25 {
        float: left;
        width: 25%;
        margin-top: 5px;
    }
    ._scol_30 {
        float: left;
        width: 30%;
        margin-top: 5px;
    }
    ._scol_35 {
        float: left;
        width: 35%;
        margin-top: 5px;
    }
    ._scol_39 {
        float: left;
        width: 39%;
        margin-top: 5px;
    }
    ._scol_40 {
        float: left;
        width: 40%;
        margin-top: 5px;
    }
    ._scol_60 {
        float: left;
        text-align: left;
        width: 60%;
        margin-top: 5px;
    }
    ._scol_80 {
        float: left;
        text-align: left;
        width: 80%;
        margin-top: 5px;
    }
    ._scol_90 {
        float: left;
        text-align: left;
        width: 90%;
        margin-top: 5px;
    }
    ._scol_99 {
        float: left;
        text-align: left;
        width: 99%;
        margin-top: 5px;
    }
    ._row {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: flex-start;
        text-align: left;
        margin: 0;
        padding: 0;
    }
    ._input, ._select, ._type_list {
        max-width: 100%;
        margin: 0 10px 0 0;
        padding: 2px 0 0 15px;
        font-size: 0.8em;
        border-style: solid;
        border-width: 1px;
        border-color: #13294B;
        background-color: #E8E9EB;
        color: black;
        box-sizing: border-box; 
        resize: vertical;
    }
    ._type_list:disabled {
        color: #006230;
        font-weight: bold;
    }
    ._item_label, ._input_label  {
        width: 100%; 
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        text-transform: capitalize;
        margin: 0;
        padding: 0 5px;
    }
    ._stmt_label {
        width: 100%; 
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        margin: 0;
        padding: 0 5px;
    }
    ._check_label {
        width: 100%; 
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        text-transform: capitalize;
        margin: 2px 0 0 0;
        padding: 0;
    }
    ._input_label:after {
        content:"";
    }
    ._delete_frame {
        display: inline-block;
        font-weight: normal;
        color: red;
        padding: 0;
    }
    ._delete_frame:hover {
        color: red;
        font-weight: bold;
        background-color: #E8E9EB;
        border-style: solid;
        border-width: 1px;
        border-color: red;
        border-radius: 4px;
    }
    ._label_delete {
        display: inline-block;
        font-size: 0.6em;
        color: red;
        letter-spacing: 1px;
        text-transform: uppercase;
        margin: 2px;
        padding: 1px;
    }
    ._add_frame {
        margin-top: 15px;
        display: inline-block;
        font-weight: normal;
        color: #006230;
        padding: 2px 0 2px 5px;
    }
    ._add_frame:hover   {
        color: #006230;
        font-weight: bold;
        background-color: #E8E9EB;
        border-style: solid;
        border-width: 1px;
        border-color: #006230;
        border-radius: 4px;
    }
    ._cancel_modal_button {
        display: inline-block;
        background-color: #13294B;
        color: white;
        border-style: solid;
        border-width: 1px;
        border-color: #13294B;
        border-radius: 4px;
        font-weight: normal;  
        margin: 0 20px;     
        padding: 1px 5px;
    }
    ._cancel_modal_button:hover {
        background-color: #FF5F05;
        color: white;
        padding: 1px 5px;
    }
    ._item_add {
        display: inline;
        width: 100%; 
        font-size: 0.8em;
        font-weight: bold;
        color: #006230;
        text-transform: none;
        text-align: left;
        margin: 0;
        padding: 2px 5px;
    }
    ._item_value   {
        width: 100%;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        text-align: left;
        margin: 0;
        padding: 0 5px;
    }
    ._item_value_ellipsis {
        width: 100%; 
        font-size: 0.8em;
        font-weight: normal;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        color: black;
        text-align: left;
        margin: 0;
        padding: 0 5px;
    }
    ._checkbox {
        display: inline;
        width: 25px;
        margin: 2px 4px 0 0;
        padding: 0;   
    }
    ._table_check_cell {
        display: table-cell;
        min-width: 10%;
        text-align: center;
        margin: 0;
        padding: 2px 0 2px 0;
    }

    ._textarea {
        width: 95%;
        max-width: 90%;
        font-size: 1.0em;
        font-weight: normal;
        text-align: left;
        padding: 5px;
        margin: 5px;
    }
    ._radio {
        display: inline-block;
        width: 16px;
        margin: 1px 10px 0 0;
        position: relative;
        top: 2px;
    }
  
    
    @media screen and (max-width: 800px) {
        ._fieldset {
            flex-wrap: wrap;  
        }
        ._fieldset_2 {
            flex-wrap: wrap;
        }
        ._col_50, ._col_60 {
            width: 100%;
        }
    }
    @media screen and (max-width: 620px) {
        ._scol_40, ._scol_60  {
            width: 100%;
            margin-top: 0;
        }
        ._col_50, ._col_60 {
            width: 100%;
        }
        ._label_delete {
            display: none;
        }
    }
    
</style>